import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Upload, AlertCircle, FileSpreadsheet, RefreshCw } from 'lucide-react';
import axiosInstance from '../../../utils/axiosConfig';
import { BUTTON } from '../utils/uiTokens';

/**
 * Importa plantillas desde un Excel o CSV (el "masterpad" de Desarrollo de
 * Productos). Primero previsualiza lo que entendio del archivo y recien al
 * confirmar guarda. Una plantilla con el mismo nombre y area que una existente
 * reemplaza sus actividades.
 */
const ImportarPlantillasModal = ({ isOpen, onClose, onImportado }) => {
  const [archivo, setArchivo] = useState(null);
  const [preview, setPreview] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState('');
  const [arrastrando, setArrastrando] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!isOpen) {
      setArchivo(null); setPreview(null); setError(''); setCargando(false);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  const enviar = async (file, guardar) => {
    setCargando(true);
    setError('');
    try {
      const fd = new FormData();
      fd.append('archivo', file);
      if (guardar) fd.append('guardar', 'true');
      const { data } = await axiosInstance.post('/api/proyecto-plantillas/importar', fd);
      if (guardar) {
        onImportado?.(data);
      } else {
        setPreview(data.plantillas || []);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'No se pudo leer el archivo.');
      if (!guardar) setPreview(null);
    } finally {
      setCargando(false);
    }
  };

  const elegir = (file) => {
    if (!file) return;
    setArchivo(file);
    enviar(file, false);
  };

  if (!isOpen) return null;

  const totalActividades = (preview || []).reduce((n, p) => n + p.actividades.length, 0);

  return createPortal(
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="w-full max-w-2xl max-h-[88vh] flex flex-col bg-white rounded-2xl shadow-2xl overflow-hidden">
        <div className="flex items-start justify-between px-6 pt-5 pb-4">
          <div>
            <h2 className="text-base font-semibold tracking-tight text-slate-900">Importar plantillas</h2>
            <p className="text-xs text-slate-500 mt-0.5">
              Una hoja por tipo de proyecto, o una columna "Tipo de proyecto". Columnas que se leen:
              N.º, Actividad, Responsable, Duración (días), Predecesora, Área, Hito.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-slate-400 hover:text-slate-700 hover:bg-slate-100 p-1.5 rounded-md transition-colors"
            aria-label="Cerrar"
          >
            <X size={16} />
          </button>
        </div>

        <div className="px-6 pb-4 overflow-y-auto min-h-0 space-y-3">
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            onDragOver={(e) => { e.preventDefault(); setArrastrando(true); }}
            onDragLeave={() => setArrastrando(false)}
            onDrop={(e) => { e.preventDefault(); setArrastrando(false); elegir(e.dataTransfer.files?.[0]); }}
            className={`w-full flex items-center justify-center gap-2 px-4 py-5 border border-dashed rounded-lg text-sm transition-colors ${
              arrastrando ? 'border-cyan-500 bg-cyan-50 text-cyan-800' : 'border-slate-300 text-slate-600 hover:bg-slate-50'
            }`}
          >
            {archivo ? <FileSpreadsheet size={16} /> : <Upload size={16} />}
            {archivo ? archivo.name : 'Elige o arrastra un archivo .xlsx o .csv'}
          </button>
          <input
            ref={inputRef}
            type="file"
            accept=".xlsx,.csv,.txt"
            className="hidden"
            onChange={(e) => { elegir(e.target.files?.[0]); e.target.value = ''; }}
          />

          {cargando && !preview && <p className="text-xs text-slate-500">Leyendo el archivo...</p>}

          {error && (
            <div className="flex items-start gap-2 px-3 py-2 bg-rose-50 border border-rose-200/70 rounded-md">
              <AlertCircle size={14} className="text-rose-500 mt-0.5 flex-shrink-0" />
              <span className="text-xs text-rose-700">{error}</span>
            </div>
          )}

          {preview && (
            <div className="border border-slate-200 rounded-lg divide-y divide-slate-100">
              {preview.map((p, i) => (
                <div key={`${p.nombre}-${p.area}-${i}`} className="px-3 py-2.5">
                  <div className="flex items-baseline justify-between gap-3">
                    <p className="text-sm font-medium text-slate-800 truncate">
                      {p.nombre}
                      {p.area && <span className="font-normal text-slate-500"> en {p.area}</span>}
                    </p>
                    <span className="text-[11px] text-slate-500 tabular-nums flex-shrink-0">
                      {p.actividades.length} actividades
                    </span>
                  </div>
                  {p.existente_id && (
                    <p className="mt-0.5 inline-flex items-center gap-1 text-[11px] text-amber-700">
                      <RefreshCw size={10} /> Ya existe: se reemplazan sus actividades
                    </p>
                  )}
                  {p.avisos.map((a) => (
                    <p key={a} className="mt-0.5 text-[11px] text-slate-500">{a}</p>
                  ))}
                  <p className="mt-1 text-[11px] text-slate-400 truncate">
                    {p.actividades.slice(0, 4).map((a) => a.titulo).join(', ')}
                    {p.actividades.length > 4 ? '...' : ''}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="flex items-center justify-between gap-2 px-6 py-3 bg-slate-50/60 border-t border-slate-200/70">
          <span className="text-[11px] text-slate-500">
            {preview ? `${preview.length} ${preview.length === 1 ? 'plantilla' : 'plantillas'}, ${totalActividades} actividades en total` : ''}
          </span>
          <div className="flex items-center gap-2">
            <button type="button" onClick={onClose} className={`${BUTTON.ghost} ${BUTTON.size.md}`}>
              Cancelar
            </button>
            <button
              type="button"
              disabled={!preview || !preview.length || cargando}
              onClick={() => enviar(archivo, true)}
              className={`${BUTTON.primary} ${BUTTON.size.md}`}
            >
              {cargando && preview ? 'Importando...' : `Importar ${preview?.length || ''} ${preview?.length === 1 ? 'plantilla' : 'plantillas'}`}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ImportarPlantillasModal;
